import React, { useCallback, useState } from "react";
import { normalizeString } from "@globals/strings";
import { cameraContainerClass, tileMode } from "@client/lib/foscam/cameraLayout";
import { useUIStore } from "@client/store/ui";
import CameraTile from "./foscam/CameraTile";
import { useOptionalPartialWrapper } from "./wrappers/PartialWrapper";

/**
 * Mřížka kamer.
 *
 * Přehrávání i zotavení z výpadku řeší `foscam/CameraStream.tsx` (go2rtc WebRTC);
 * jedna dlaždice je `foscam/CameraTile.tsx`. Tady zůstává jen rozložení a to, která
 * kamera je zvětšená.
 *
 * Rozložení má dvě nezávislé osy — režim zvolený uživatelem (přepínač v hlavičce
 * wrapperu, drží ho `useUIStore.foscamLayout`) a fullscreen. Konkrétní třídy počítá
 * `lib/foscam/cameraLayout.ts`, aby to šlo otestovat.
 *
 *  - `auto`  → normálně vedle sebe, ve fullscreenu pod sebou se snap scrollem
 *  - `quad`  → mřížka 2×2 v obou stavech
 *
 * Ve všech režimech kromě `auto`-normálně má dlaždice plné PTZ ovládání, protože
 * obraz vyplňuje buňku a je pro něj místo.
 */

interface FoscamsProps {
  foscams: FoscamReact[];
  topicIds?: string[];
}

/** Stabilní prázdné pole — `?? []` v renderu by měnilo identitu a rozbíjelo memoizace. */
const NO_HIDDEN: boolean[] = [];

const Foscams: React.FC<FoscamsProps> = ({ foscams }) => {
  // Optional varianta: kdyby widget skončil mimo wrapper (layout editor), nemá to
  // shodit celý dashboard — jen se nebude nic skrývat ani stackovat.
  const wrapper = useOptionalPartialWrapper();
  const hidden = wrapper?.hidden ?? NO_HIDDEN;
  const isFullscreen = wrapper?.isFullscreen ?? false;
  const layoutMode = useUIStore((s) => s.foscamLayout);
  const tm = tileMode(layoutMode, isFullscreen);

  const [spotlightSlug, setSpotlightSlug] = useState<string | null>(null);

  const closeSpotlight = useCallback(() => setSpotlightSlug(null), []);

  /**
   * Spotlight nesmí přežít zmizení své kamery — ani skrytím z hlavičky wrapperu,
   * ani změnou seznamu kamer; jinak by zůstal fixed overlay bez obsahu.
   *
   * Řeší se DERIVACÍ, ne efektem se setState: stará hodnota ve state je neškodná,
   * protože se nikde nepoužije, a nevzniká kaskáda renderů.
   */
  const spotlightIndex = spotlightSlug
    ? foscams.findIndex((f) => normalizeString(f.name) === spotlightSlug)
    : -1;
  const activeSpotlight = spotlightIndex !== -1 && !hidden[spotlightIndex] ? spotlightSlug : null;

  return (
    <div className={cameraContainerClass(layoutMode, isFullscreen)}>
      {foscams.map((foscam, index) => {
        if (hidden[index]) return null;
        const slug = normalizeString(foscam.name);
        return (
          <CameraTile
            key={foscam.name}
            slug={slug}
            name={foscam.name}
            mode={tm}
            spotlighted={activeSpotlight === slug}
            onSpotlight={() => setSpotlightSlug(slug)}
            onExitSpotlight={closeSpotlight}
          />
        );
      })}
    </div>
  );
};

export default Foscams;
